import type { DebugManager } from '../debug-manager.class';

export interface ShowTooltipParams {
	id: string;
	screenX: number;
	screenY: number;
}

/**
 * Show tooltip for a sphere at screen position
 */
export const showTooltipCfn = (
	self: DebugManager,
	{ id, screenX, screenY }: ShowTooltipParams
): void => {
	const tooltip = self.getTooltip(id);
	const label = self.getSphereLabel(id);
	const sphere = self.getSphere(id);

	if (tooltip && label && sphere) {
		const worldPos = sphere.position;
		tooltip.style.left = `${screenX}px`;
		tooltip.style.top = `${screenY}px`;
		tooltip.style.transform = 'translate(-50%, -120%)';
		tooltip.style.display = 'block';
		tooltip.textContent = `${label}: (${worldPos.x.toFixed(1)}, ${worldPos.y.toFixed(1)}, ${worldPos.z.toFixed(1)})`;
	}
};

export interface HideTooltipParams {
	id: string;
}

/**
 * Hide tooltip
 */
export const hideTooltipCfn = (
	self: DebugManager,
	{ id }: HideTooltipParams
): void => {
	const tooltip = self.getTooltip(id);
	if (tooltip) {
		tooltip.style.display = 'none';
	}
};

/**
 * Hide all tooltips
 */
export const hideAllTooltipsCfn = (
	self: DebugManager
): void => {
	for (const tooltip of self.tooltips.values()) {
		tooltip.style.display = 'none';
	}
};

/**
 * Update tooltip positions (call in render loop)
 */
export const updateTooltipPositionsCfn = (
	self: DebugManager
): void => {
	if (!self.visible) return;

	const { camera, renderer } = self.configAccess;

	for (const [id, tooltip] of self.tooltips) {
		if (tooltip.style.display === 'none') continue;

		const sphere = self.getSphere(id);
		if (!sphere) continue;

		// Project world position to screen
		const worldPos = sphere.position.clone();
		const projected = worldPos.project(camera);

		const rect = renderer.domElement.getBoundingClientRect();
		const x = rect.left + (projected.x * 0.5 + 0.5) * rect.width;
		const y = rect.top + (-projected.y * 0.5 + 0.5) * rect.height;

		tooltip.style.left = `${x}px`;
		tooltip.style.top = `${y}px`;
	}
};

export interface CreateTooltipParams {
	id: string;
}

/**
 * Create a tooltip element
 */
export const createTooltipCfn = (
	self: DebugManager,
	{ id }: CreateTooltipParams
): void => {
	if (self.hasTooltip(id)) return;

	const tooltip = document.createElement('div');
	tooltip.style.position = 'fixed';
	tooltip.style.zIndex = '1000';
	tooltip.style.pointerEvents = 'none';
	tooltip.style.padding = '2px 6px';
	tooltip.style.borderRadius = '4px';
	tooltip.style.background = 'rgba(0,0,0,0.7)';
	tooltip.style.color = '#fff';
	tooltip.style.fontSize = '11px';
	tooltip.style.lineHeight = '1.2';
	tooltip.style.whiteSpace = 'nowrap';
	tooltip.style.display = 'none';

	document.body.appendChild(tooltip);
	self.registerTooltip(id, tooltip);
};

export interface RemoveTooltipParams {
	id: string;
}

/**
 * Remove a tooltip element
 */
export const removeTooltipCfn = (
	self: DebugManager,
	{ id }: RemoveTooltipParams
): void => {
	const tooltip = self.getTooltip(id);
	if (tooltip) {
		if (tooltip.parentElement) {
			tooltip.parentElement.removeChild(tooltip);
		}
		self.unregisterTooltip(id);
	}
};
